import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Tabs } from '../../../components/ui/index.js';
import { useDaemonScope } from './DaemonScopeContext.js';
import { daemonReads } from './daemon-queries.js';
import {
  AUDIT_EMPTY_LABELS,
  AUDIT_TABS,
  AuditTabId,
  RunReport,
  STATUS_VARIANTS,
  asArray,
  asRecord,
  formatCommand,
} from './run-audit-shared.js';

export const RunAuditTabs: React.FC<{ daemonId: number; runId: number }> = ({ daemonId, runId }) => {
  const { scope } = useDaemonScope();
  const [activeTab, setActiveTab] = useState<AuditTabId>('overview');

  const auditQuery = useQuery(daemonReads(scope).runAudit(daemonId, runId));

  const audit = asRecord(asRecord(auditQuery.data).data);
  const overview = asRecord(audit.overview);
  const rows = {
    steps: asArray(audit.steps),
    tools: asArray(audit.tools),
    resourceAccess: asArray(audit.resourceAccess),
    sources: asArray(audit.sources),
    fragments: asArray(audit.sourceFragments),
    integrity: asArray(audit.integrityChecks),
    candidates: asArray(audit.candidates),
    reviewQueue: asArray(audit.reviewQueue),
    findings: asArray(audit.findings),
    versions: asArray(audit.versions),
    decisions: asArray(audit.decisions),
    reports: asArray(audit.reports),
    artifacts: asArray(audit.artifacts),
  };

  if (auditQuery.isLoading) return <div className="text-xs text-muted">Loading run audit...</div>;
  if (auditQuery.isError) return <div className="text-xs text-danger">Could not load run audit.</div>;

  return (
    <div className="rounded-sm border bg-surface p-3">
      <Tabs
        variant="segmented"
        aria-label="Run audit sections"
        activeTab={activeTab}
        onChange={(id) => setActiveTab(id as AuditTabId)}
        className="mb-3"
        tabs={AUDIT_TABS.map((tab) => ({
          id: tab.id,
          label: tab.label,
          icon: tab.icon,
          count: tab.id === 'overview' ? undefined : (rows[tab.id as keyof typeof rows]?.length ?? 0),
        }))}
      />

      {activeTab === 'overview' ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
          {Object.entries(overview).map(([key, value]) => (
            <div key={key} className="rounded-sm border bg-surface-alt p-2">
              <div className="text-xs text-muted">{key}</div>
              <div className="text-body font-medium">{String(value)}</div>
            </div>
          ))}
        </div>
      ) : (
        <AuditRows rows={rows[activeTab as keyof typeof rows] ?? []} emptyLabel={AUDIT_EMPTY_LABELS[activeTab]} />
      )}
    </div>
  );
};

export const AuditRows: React.FC<{ rows: unknown[]; emptyLabel: string }> = ({ rows, emptyLabel }) => {
  if (rows.length === 0) return <div className="text-xs text-muted">{emptyLabel}</div>;
  return (
    <div className="space-y-2 max-h-96 overflow-y-auto">
      {rows.map((row, index) => {
        const record = asRecord(row);
        const title =
          record.title ??
          record.name ??
          record.commandName ??
          record.stepName ??
          record.message ??
          record.diffSummary ??
          record.canonicalKey ??
          record.fragmentKey ??
          record.checkType ??
          record.id ??
          `Row ${index + 1}`;
        const status = firstNonEmptyString(record.status, record.classification, record.severity);
        const meta = auditRowMeta(record);
        return (
          <details key={String(record.id ?? index)} className="rounded-sm border bg-surface-alt">
            <summary className="cursor-pointer p-2 text-sm font-medium text-body flex items-start justify-between gap-2">
              <span className="min-w-0">
                <span className="block truncate">{String(title)}</span>
                {meta.length > 0 && (
                  <span className="mt-1 block truncate text-xs font-normal text-muted">{meta.join(' / ')}</span>
                )}
              </span>
              {status ? (
                <Badge variant={auditStatusVariant(status)} size="sm">
                  {status}
                </Badge>
              ) : null}
            </summary>
            <pre className="text-xs border-t p-2 overflow-auto max-h-64">{JSON.stringify(record, null, 2)}</pre>
          </details>
        );
      })}
    </div>
  );
};

export function firstNonEmptyString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

export function auditRowMeta(record: Record<string, unknown>): string[] {
  const meta: string[] = [];
  for (const key of [
    'targetType',
    'sourceType',
    'fragmentType',
    'checkType',
    'eventType',
    'action',
    'resourceType',
    'resourceId',
    'risk',
    'candidateId',
    'sourceId',
    'version',
  ]) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== '') {
      meta.push(`${key}: ${String(value).replaceAll('_', ' ')}`);
    }
  }
  return meta.slice(0, 4);
}

export function auditStatusVariant(status: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' {
  if (['completed', 'ready_for_human', 'passed', 'active', 'applied'].includes(status)) return 'success';
  if (
    status === 'blocked' ||
    status === 'denied' ||
    status === 'critical' ||
    status === 'high' ||
    status.endsWith('_failed')
  ) {
    return 'danger';
  }
  if (status === 'warning' || status === 'locked' || status === 'superseded' || status.startsWith('pending')) {
    return 'warning';
  }
  return STATUS_VARIANTS[status] ?? 'neutral';
}

export const ReportAuditDetails: React.FC<{ report: RunReport }> = ({ report }) => {
  const source = asRecord(report.source);
  const toolRuns = asArray(report.toolRuns);
  const artifactIds = asArray(report.artifactIds);
  const findingIds = asArray(report.findingIds);
  const decisionIds = asArray(report.decisionIds);
  const hasSource = Object.keys(source).length > 0;
  const hasAuditData =
    hasSource || toolRuns.length > 0 || artifactIds.length > 0 || findingIds.length > 0 || decisionIds.length > 0;

  if (!hasAuditData) return null;

  return (
    <div className="mt-3 space-y-2 text-xs">
      {hasSource && (
        <div className="rounded-sm border bg-surface p-2">
          <div className="font-medium text-body mb-1">Source</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-muted">
            {Object.entries(source).map(([key, value]) => (
              <span key={key} className="break-all">
                {key}: {String(value)}
              </span>
            ))}
          </div>
        </div>
      )}

      {toolRuns.length > 0 && (
        <div className="rounded-sm border bg-surface p-2">
          <div className="font-medium text-body mb-1">Tool Commands</div>
          <div className="space-y-1">
            {toolRuns.map((toolRun, index) => {
              const run = asRecord(toolRun);
              return (
                <div key={index} className="text-muted">
                  <span className="text-body">{String(run.name ?? `tool ${index + 1}`)}</span>
                  {run.version ? <span> v{String(run.version)}</span> : null}
                  <span> / exit {String(run.exit_code ?? run.exitCode ?? 'n/a')}</span>
                  <span> / {String(run.duration_ms ?? run.durationMs ?? 'n/a')}ms</span>
                  <div className="font-mono break-all mt-0.5">{formatCommand(run.command)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-muted">
        {artifactIds.length > 0 && <span>Artifacts: {artifactIds.map(String).join(', ')}</span>}
        {findingIds.length > 0 && <span>Findings: {findingIds.map(String).join(', ')}</span>}
        {decisionIds.length > 0 && <span>Decisions: {decisionIds.map(String).join(', ')}</span>}
      </div>
    </div>
  );
};
